// Client for the shared CaddieGames course catalog (backend Cosmos store,
// routes at /caddiegames/courses). This is the DB-backed source of truth —
// distinct from coursesDb.ts, which is the per-browser localStorage layer.
//
// The CGAdmin console talks to this directly (curation). The player app will
// later read the catalog as its base layer and keep local edits/additions on
// top (coursesDb), submitting them as moderated proposals.

import { Course } from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/caddiegames/courses`;

async function readJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data;
}

// The backend returns courses without their editLog (see COURSE_PUBLIC_FIELDS),
// which is exactly the Course shape plus ownership/version metadata we ignore
// here. Cast to Course — the extra fields are harmless.
function toCourse(doc: any): Course {
  return doc as Course;
}

export interface CourseQuery {
  q?: string;
  country?: string;
  region?: string;
  offset?: number;
  limit?: number;
}

// Server-side paginated + filtered search. Returns one page plus `hasMore`.
// This is the scalable path (works past the old 200-course ceiling).
export async function queryCourses(
  params: CourseQuery = {},
): Promise<{ courses: Course[]; hasMore: boolean; total?: number }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.country) qs.set("country", params.country);
  if (params.region) qs.set("region", params.region);
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.limit) qs.set("limit", String(params.limit));
  const res = await fetch(`${BASE}?${qs.toString()}`, {
    credentials: "include",
  });
  const data = await readJson(res);
  return {
    courses: Array.isArray(data.courses) ? data.courses.map(toCourse) : [],
    hasMore: !!data.hasMore,
    total: typeof data.total === "number" ? data.total : undefined,
  };
}

// Convenience: first page of matches as a plain array (used by the offline
// cache warm-up and any caller that doesn't paginate).
export async function searchCourses(q = ""): Promise<Course[]> {
  const { courses } = await queryCourses({ q, limit: 200 });
  return courses;
}

// Distinct Country / Region values for the Advanced-search dropdowns. Region
// list narrows to the given country when supplied.
export async function getCourseFacets(
  country = "",
): Promise<{ countries: string[]; regions: string[] }> {
  const qs = country ? `?country=${encodeURIComponent(country)}` : "";
  const res = await fetch(`${BASE}/facets${qs}`, { credentials: "include" });
  const data = await readJson(res);
  return {
    countries: Array.isArray(data.countries) ? data.countries : [],
    regions: Array.isArray(data.regions) ? data.regions : [],
  };
}

export async function getCourse(id: string): Promise<Course> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  const data = await readJson(res);
  return toCourse(data.course);
}

// Add a new catalog card. The server owns the id + ownership + first audit
// line, so the returned course carries the authoritative id.
export async function createCourse(
  course: Course,
  displayName?: string,
): Promise<Course> {
  const res = await fetch(BASE, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...course, displayName }),
  });
  const data = await readJson(res);
  return toCourse(data.course);
}

// Edit an existing card. Throws on a 409 (someone else edited it first) so the
// caller can prompt a reopen/retry.
export async function updateCourse(
  id: string,
  course: Course,
  displayName?: string,
): Promise<Course> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...course, displayName }),
  });
  const data = await readJson(res);
  return toCourse(data.course);
}

// Admin-only delete.
export async function deleteCatalogCourse(id: string): Promise<void> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  await readJson(res);
}

// One-time bulk import of the shipped course library. Idempotent server-side
// (skips ids already present), so re-running never wipes edits.
export async function seedCourses(
  courses: Course[],
): Promise<{ created: number; skipped: number }> {
  const res = await fetch(`${BASE}/seed`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ courses }),
  });
  const data = await readJson(res);
  return { created: data.created ?? 0, skipped: data.skipped ?? 0 };
}

// ── Enrichment: AI-suggested course improvements → admin review ────────────

export interface EnrichmentMeta {
  websiteUrl?: string;
  scorecardUrl?: string;
  scorecardKind?: string;
  checkedSources?: string[];
}

export interface EnrichmentStageLog {
  stage: string;
  detail?: string;
  atMs: number;
  elapsedMs: number;
}

export interface EnrichmentResult {
  status: "suggestions" | "no-source-found" | "error";
  courseId: string;
  provider: string;
  current: Course;
  suggested?: Course;
  changes: string[];
  meta?: EnrichmentMeta;
  timeline: EnrichmentStageLog[];
  totalMs: number;
  error?: string;
}

// Run the Course Enrichment Service for one catalog course. Returns SUGGESTED
// changes only — it never writes. The admin reviews + approves through the
// normal edit path. Long-running (the backend searches the web, fetches the
// scorecard, and extracts it), so callers should show progress.
export async function enrichCourse(
  id: string,
  sourceUrl?: string,
): Promise<EnrichmentResult> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/enrich`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sourceUrl ? { sourceUrl } : {}),
  });
  return readJson(res) as Promise<EnrichmentResult>;
}

// ── Moderation: player proposals → admin review ────────────────────────────

export interface CourseProposal {
  id: string;
  kind: "edit" | "new";
  courseId: string;
  proposed: Course;
  baseVersion?: number;
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  status: "pending" | "approved" | "rejected";
}

// Player submits a local edit / new card for admin review. Fire-and-forget from
// the caller's view — a failure here must not block the local save.
export async function submitProposal(input: {
  kind: "edit" | "new";
  courseId: string;
  course: Course;
  baseVersion?: number;
  displayName?: string;
}): Promise<void> {
  const res = await fetch(`${BASE}/proposals`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      kind: input.kind,
      courseId: input.courseId,
      course: input.course,
      baseVersion: input.baseVersion,
      displayName: input.displayName,
    }),
  });
  await readJson(res);
}

// Admin: pending review queue + count (for the bell).
export async function listProposals(): Promise<{
  proposals: CourseProposal[];
  count: number;
}> {
  const res = await fetch(`${BASE}/proposals`, { credentials: "include" });
  const data = await readJson(res);
  return {
    proposals: Array.isArray(data.proposals) ? data.proposals : [],
    count: data.count ?? 0,
  };
}

// Admin: approve — push the (optionally admin-edited) card into the catalog.
export async function approveProposal(
  id: string,
  course?: Course,
  displayName?: string,
): Promise<void> {
  const res = await fetch(
    `${BASE}/proposals/${encodeURIComponent(id)}/approve`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ course, displayName }),
    },
  );
  await readJson(res);
}

// Admin: reject — the change stays local to the submitter.
export async function rejectProposal(
  id: string,
  displayName?: string,
): Promise<void> {
  const res = await fetch(`${BASE}/proposals/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ displayName }),
  });
  await readJson(res);
}
